import { useQuery } from '@tanstack/react-query'
import { ShieldAlert } from 'lucide-react'
import { signalsApi } from '../api/client'
import { relativeTime } from '../lib/relativeTime'
import type { Book } from '../types'
import './CapRejectionsFeed.css'

interface CapRejectionsFeedProps {
  book: Book
}

const LIVE_CAP_RE = /live_cap/i

const CAPS_NOTE =
  'Inviolable gross/per-order caps are enforced in the execution router from ' +
  'terraform-set env values; the limits themselves are not exposed via the API ' +
  '(never displayed here to avoid showing a stale or invented number).'

/**
 * Feed of recent live-cap rejections (inviolable gross/per-order caps
 * enforced in the execution router). Deliberately does not fetch or show
 * the cap dollar values themselves — those are terraform-set env values not
 * exposed via the API, so showing a number here risks a stale or invented
 * figure. Honest-by-omission.
 */
export function CapRejectionsFeed({ book }: CapRejectionsFeedProps) {
  const { data, isLoading, isError } = useQuery({
    queryKey: ['signal-history-caps', book.id],
    queryFn: () => signalsApi.getHistory(50),
    retry: 1,
    refetchInterval: 30000,
  })

  const rejections = (data ?? []).filter(
    (s) => s.status === 'rejected' && s.rejected_reason && LIVE_CAP_RE.test(s.rejected_reason)
  )

  return (
    <div className="card cap-rejections-feed">
      <div className="card-header">
        <h3 className="card-title">
          <ShieldAlert size={18} className="icon" aria-hidden="true" />
          Live-cap rejections
        </h3>
      </div>

      {isLoading ? (
        <div className="loading"><div className="spinner" /></div>
      ) : isError ? (
        <div className="empty-state">
          <div className="empty-icon"><ShieldAlert size={28} aria-hidden="true" /></div>
          <div className="empty-text">signal history unavailable</div>
        </div>
      ) : rejections.length === 0 ? (
        <div className="empty-state">
          <div className="empty-icon"><ShieldAlert size={28} aria-hidden="true" /></div>
          <div className="empty-text">No live-cap rejections.</div>
          <p className="caption cap-rejections-note">{CAPS_NOTE}</p>
        </div>
      ) : (
        <>
          <ul className="cap-rejections-list">
            {rejections.map((r) => (
              <li key={r.id} className="cap-rejections-row">
                <span className="mono cap-rejections-symbol">{r.symbol}</span>
                <span className="cap-rejections-reason">{r.rejected_reason}</span>
                <span className="caption cap-rejections-time">{relativeTime(r.rejected_at)}</span>
              </li>
            ))}
          </ul>
          <p className="caption cap-rejections-note">{CAPS_NOTE}</p>
        </>
      )}
    </div>
  )
}
